// src/constants/menus/index.ts
export * from './icons';
export * from './utils';
export * from './menu-config';
export * from './menu-builder';

import { MenuItemType, UserRole, UserGroup } from './types';
import { buildMenuForRoles, buildMenuForRole, buildMenuForGroups, MenuBuilder, PermissionChecker } from './menu-builder';
import { MENU_CONFIGS, ROLE_TO_GROUPS } from './menu-config';
import { removeDuplicateMenuItems } from './utils';

// Core menus (keep for backward compatibility)
export { HEADER_MENU_ITEMS, WIDGET_ITEMS } from './core-menus';

// Main API
export const getMenuByRoles = (roles: UserRole[]): MenuItemType[] => {
  return buildMenuForRoles(roles);
};

export const getMenuByRole = (role: UserRole): MenuItemType[] => {
  return buildMenuForRole(role);
};

export const getMenuByGroups = (groups: UserGroup[]): MenuItemType[] => {
  return buildMenuForGroups(groups);
};

// Backward compatibility
export const GROUP_TO_MENU_ITEMS: Record<string, MenuItemType[]> = {
  PRIMARY_USERS: buildMenuForGroups(['PRIMARY_USERS']),
  ADMIN_USERS: buildMenuForGroups(['ADMIN_USERS']),
  MONITORING_TEAM: buildMenuForGroups(['MONITORING_TEAM']),
  SUPPORT_MONITORING: buildMenuForGroups(['SUPPORT_MONITORING']),
  GUEST_USERS: buildMenuForGroups(['GUEST_USERS']),
  BUSINESS_DEVELOPMENT: buildMenuForGroups(['BUSINESS_DEVELOPMENT']),
  RISK_SECURITY: buildMenuForGroups(['RISK_SECURITY']),
};

export const SIDEBAR_MENU_ITEMS: Record<string, MenuItemType[]> = {
  right_owner: buildMenuForRole('right_owner'),
  licensee: buildMenuForRole('licensee'),
  admin: buildMenuForRole('admin'),
  monitor: buildMenuForRole('monitor'),
  legal: buildMenuForRole('legal'),
  guest: buildMenuForRole('guest'),
  all: buildMenuForRoles(['admin']),
};

// Feature-specific helpers
export const getCertificateMenuByRole = (role: UserRole): MenuItemType[] => {
  const builder = new MenuBuilder([role]);
  const allMenus = builder.buildAll();
  return allMenus.filter(m => m.key === 'certificates');
};

export const getMarketplaceMenuByRole = (role: UserRole): MenuItemType[] => {
  const builder = new MenuBuilder([role]);
  const allMenus = builder.buildAll();
  return allMenus.filter(m => m.key === 'marketplace' || m.key === 'marketplace-public');
};

export const getPaymentMenuByRole = (role: UserRole): MenuItemType[] => {
  const builder = new MenuBuilder([role]);
  const allMenus = builder.buildAll();
  return allMenus.filter(m => m.key === 'payments-revenue' || m.key === 'admin-payments');
};

// Permission helpers
export const canAccessPayments = (role: UserRole) => PermissionChecker.canAccessPayments(role);
export const canManagePayments = (role: UserRole) => PermissionChecker.canManagePayments(role);
export const canPublishContent = (role: UserRole) => PermissionChecker.canPublishContent(role);
export const canApproveCertificates = (role: UserRole) => PermissionChecker.canApproveCertificates(role);
export const canAccessMarketplace = (role: UserRole) => PermissionChecker.canAccessMarketplace(role);
export const canAccessCreatorTools = (role: UserRole) => PermissionChecker.canAccessCreatorTools(role);

export default {
  getMenuByRoles,
  getMenuByRole,
  getMenuByGroups,
  SIDEBAR_MENU_ITEMS,
  GROUP_TO_MENU_ITEMS,
  MenuBuilder,
  PermissionChecker,
  getCertificateMenuByRole,
  getMarketplaceMenuByRole,
  getPaymentMenuByRole,
};